import { useState, useEffect } from "react";
import { useAuth, api } from "@/App";
import { useAppMode } from "@/context/AppModeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import AiAssistButton from "@/components/AiAssistButton";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { DollarSign, Calendar, Clock, MapPin, Loader2, Heart, Car, ExternalLink } from "lucide-react";
import { DatePickerField, TimePickerField } from "@/components/DateTimePicker";
import { useLocale } from "@/i18n/LocaleContext";

export default function SendOfferModal({ isOpen, onClose, woman, onSuccess, initialData }) {
  const { user } = useAuth();
  const { isDluxe } = useAppMode();
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState(0);
  const [isRepeat, setIsRepeat] = useState(false);

  useEffect(() => {
    if (isOpen && woman?.user_id) {
      api.get(`/date-offers/repeat-check/${woman.user_id}`)
        .then((res) => setIsRepeat(res.data.repeat))
        .catch(() => setIsRepeat(false));
    }
  }, [isOpen, woman?.user_id]);
  const [formData, setFormData] = useState({
    offer_amount: "",
    proposed_date: "",
    proposed_time: "",
    date_type: "",
    location: "",
    message: "",
    transport_allowance: "0",
    experience_url: "",
    duration_hours: ""
  });

  useEffect(() => {
    if (isOpen) {
      fetchAvailableSlots();
      // Reset form (optionally pre-filled, e.g. from a date idea)
      setFormData({
        offer_amount: "",
        proposed_date: "",
        proposed_time: "",
        date_type: "",
        location: "",
        message: "",
        transport_allowance: "0",
        experience_url: "",
        duration_hours: "",
        ...(initialData || {})
      });
    }
  }, [isOpen, initialData]);

  const fetchAvailableSlots = async () => {
    try {
      const res = await api.get("/premium-slots/status");
      setAvailableSlots(res.data.available_slots || 0);
    } catch (error) {
      console.error("Failed to fetch slots:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (availableSlots === 0 && !isRepeat && !woman?.is_ai) {
      toast.error("You need to purchase a premium slot first!");
      return;
    }

    const amount = parseFloat(formData.offer_amount);
    if (amount < 100 || amount > 1000) {
      toast.error("Offer amount must be between $100 and $1000");
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/date-offers/send", {
        to_user_id: woman.user_id,
        offer_amount: amount,
        proposed_date: formData.proposed_date,
        proposed_time: formData.proposed_time,
        date_type: formData.date_type || "",
        location: formData.location,
        message: formData.message || "",
        transport_allowance: parseFloat(formData.transport_allowance) || 0,
        experience_url: formData.experience_url || "",
        duration_hours: formData.duration_hours ? parseFloat(formData.duration_hours) : null
      });

      console.log("✅ Offer sent successfully:", response.data);
      toast.success(`Offer sent to ${woman.name}!`);
      
      // Close modal first
      onClose();
      
      // Then trigger success callback
      if (onSuccess) {
        setTimeout(() => onSuccess(), 300);
      }
    } catch (error) {
      console.error("❌ Failed to send offer:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to send offer");
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!woman) return null;

  const transport = parseFloat(formData.transport_allowance) || 0;
  const total = (parseFloat(formData.offer_amount) || 0) + transport;
  const airbnbCity = (formData.location || woman.location || "").split(",")[0].trim();
  const airbnbUrl = airbnbCity
    ? `https://www.airbnb.com/s/${encodeURIComponent(airbnbCity)}/experiences${formData.proposed_date ? `?checkin=${formData.proposed_date}&checkout=${formData.proposed_date}` : ""}`
    : `https://www.airbnb.com/s/experiences${formData.proposed_date ? `?checkin=${formData.proposed_date}&checkout=${formData.proposed_date}` : ""}`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("offerModal.title")}</DialogTitle>
          <DialogDescription>
            {t("offerModal.desc").replace("{name}", woman.name)}
          </DialogDescription>
        </DialogHeader>

        {availableSlots === 0 && !isRepeat && !woman?.is_ai ? (
          <div className="py-8 text-center">
            <p className="text-muted-foreground mb-4">
              {t("offerModal.needSlot")}
            </p>
            <Button onClick={() => {
              onClose();
              window.location.href = "/premium-offers";
            }}>
              {t("offerModal.purchaseSlot")}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {woman?.is_ai && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-400" data-testid="test-offer-banner">
                {t("offerModal.testBanner").replace("{name}", woman.name)}
              </div>
            )}
            {isRepeat && !woman?.is_ai && (
              <div className="bg-green-500/10 border border-green-500/25 rounded-lg p-3 text-sm text-green-400" data-testid="repeat-date-banner">
                🔁 Repeat date with {woman.name}! No slot needed — and {isDluxe ? "he" : "she"} receives 90% of your offer (only 10% platform fee).
              </div>
            )}
            <div className="bg-primary/10 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <img
                  src={woman.photos?.[0] || woman.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(woman.name || "User")}&background=b91c1c&color=fff&size=100`}
                  alt={woman.name}
                  className="h-12 w-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold">{woman.name}</p>
                  {woman.age ? <p className="text-sm text-muted-foreground">{woman.age} {t("common.years")}</p> : null}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {woman?.is_ai ? t("offerModal.testNoSlot") : isRepeat ? t("offerModal.repeatNoSlot") : t("offerModal.slotsAvailable").replace("{n}", availableSlots)}
              </p>
            </div>

            <div>
              <Label htmlFor="offer_amount">
                <DollarSign className="inline h-4 w-4" /> {t("offerModal.amount")}
              </Label>
              <Input
                id="offer_amount"
                type="number"
                min="100"
                max="1000"
                step="10"
                placeholder={t("offerModal.amountPlaceholder")}
                value={formData.offer_amount}
                onChange={(e) => handleChange("offer_amount", e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("offerModal.amountHint").replace("{fee}", isRepeat ? "10" : "20")}
              </p>
            </div>

            <div>
              <Label htmlFor="transport_allowance">
                <Car className="inline h-4 w-4" /> {t("offerModal.transport")}
              </Label>
              <select
                id="transport_allowance"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={formData.transport_allowance}
                onChange={(e) => handleChange("transport_allowance", e.target.value)}
                data-testid="transport-allowance-select"
              >
                <option value="0">None</option>
                {[10, 20, 30, 50, 75, 100].map((v) => (
                  <option key={v} value={v}>${v} — Uber/taxi to the venue</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {t("offerModal.transportHint")}
              </p>
            </div>

            <div>
              <Label htmlFor="proposed_date">
                <Calendar className="inline h-4 w-4" /> {t("offerModal.proposedDate")}
              </Label>
              <DatePickerField
                id="offer-date-picker"
                value={formData.proposed_date}
                onChange={(v) => handleChange("proposed_date", v)}
              />
            </div>

            <div>
              <Label htmlFor="proposed_time">
                <Clock className="inline h-4 w-4" /> {t("offerModal.proposedTime")}
              </Label>
              <TimePickerField
                id="offer-time-picker"
                value={formData.proposed_time}
                onChange={(v) => handleChange("proposed_time", v)}
              />
            </div>

            <div>
              <Label htmlFor="duration_hours">
                <Clock className="inline h-4 w-4" /> {t("offerModal.duration")}
              </Label>
              <select
                id="duration_hours"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={formData.duration_hours}
                onChange={(e) => handleChange("duration_hours", e.target.value)}
                data-testid="offer-duration-select"
              >
                <option value="">{t("form.openEnded")}</option>
                {[1, 1.5, 2, 2.5, 3, 4, 5, 6, 8].map((h) => (
                  <option key={h} value={h}>{h} hour{h === 1 ? "" : "s"}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {t("offerModal.durationHint")}
              </p>
            </div>

            <div>
              <Label htmlFor="location">
                <MapPin className="inline h-4 w-4" /> {t("offerModal.location")}
              </Label>
              <LocationAutocomplete
                id="location"
                placeholder={t("form.searchVenue")}
                value={formData.location}
                onChange={(v) => handleChange("location", v)}
                required
              />
            </div>

            <div>
              <Label htmlFor="date_type">
                <Heart className="inline h-4 w-4" /> {t("offerModal.dateType")}
              </Label>
              <select
                id="date_type"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={formData.date_type}
                onChange={(e) => handleChange("date_type", e.target.value)}
                required
                data-testid="date-type-select"
              >
                <option value="">{t("offerModal.selectDateType")}</option>
                <option value="dinner">🍽️ Dinner</option>
                <option value="lunch">🥗 Lunch</option>
                <option value="coffee">☕ Coffee / Drinks</option>
                <option value="movie">🎬 Movie</option>
                <option value="activity">🎯 Activity / Adventure</option>
                <option value="concert">🎵 Concert / Show</option>
                <option value="wine_tasting">🍷 Wine Tasting</option>
                <option value="spa">💆 Spa / Wellness</option>
                <option value="shopping">🛍️ Shopping</option>
                <option value="gallery">🎨 Art Gallery / Museum</option>
                <option value="sports">⚽ Sports Event</option>
                <option value="beach">🏖️ Beach / Outdoor</option>
                <option value="airbnb_experience">🏡 Airbnb Experience</option>
                <option value="custom">✨ Custom / Other</option>
              </select>
            </div>

            {formData.date_type === "airbnb_experience" && (
              <div className="rounded-lg border border-rose-400/30 bg-rose-500/5 p-3 space-y-2" data-testid="airbnb-experience-box">
                <a
                  href={airbnbUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm font-medium text-rose-400 hover:text-rose-300"
                  data-testid="browse-airbnb-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  Browse live Airbnb Experiences{airbnbCity ? ` in ${airbnbCity}` : ""}{formData.proposed_date ? " (your date pre-filled)" : ""}
                </a>
                <Input
                  placeholder="Paste the Airbnb Experience link here (optional)"
                  value={formData.experience_url}
                  onChange={(e) => handleChange("experience_url", e.target.value)}
                  data-testid="airbnb-experience-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  Pick an experience on Airbnb, then paste its link so {woman.name} can see exactly what you&apos;re planning.
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="message">{t("offerModal.message")}</Label>
              <Textarea
                id="message"
                placeholder={t("form.addPersonalMessage")}
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
                rows={3}
              />
              <div className="mt-2">
                <AiAssistButton
                  context="offer_message"
                  receiverId={woman?.user_id}
                  extra={{ amount: formData.offer_amount, location: formData.location }}
                  onPick={(text) => handleChange("message", text)}
                  label="write my note"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="send-offer-submit-btn">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("offerModal.sendOffer")}{total > 0 ? ` ($${total.toFixed(0)}${transport > 0 ? " incl. transport" : ""})` : ""}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
